// Tagging state: find records missing tags, get a suggestion for one, and apply it to Discogs
import { ref, computed } from 'vue';
import useCollection from '@/composables/useCollection';
import { suggestTags, applyTags } from '@/services/discogs';

// Which tags a record still lacks. hasOriginalYear is undefined in caches saved before the field
// existed; that means "unknown", not "missing", until the next refresh.
export const missingFor = record => ({
  genres: !(record.genres || []).length,
  year: record.hasOriginalYear === false,
});

export const needsTagging = record => {
  const missing = missingFor(record);
  return missing.genres || missing.year;
};

const errorMessage = err => err.response?.data?.error || 'Something went wrong. Try again.';

export default function useTagging() {
  const {
    releases, isLoading, error, loadCollection, updateRelease,
  } = useCollection();
  // Per-record working state, keyed by instanceId: { status, suggestion, genres, year, message }
  const work = ref({});

  const flagged = computed(() => releases.value
    .map(record => ({ record, missing: missingFor(record) }))
    .filter(({ missing }) => missing.genres || missing.year));

  const stateFor = instanceId => work.value[instanceId] || { status: 'idle' };
  const setState = (instanceId, state) => { work.value = { ...work.value, [instanceId]: state }; };

  const suggest = async record => {
    setState(record.instanceId, { status: 'loading' });
    try {
      const suggestion = await suggestTags(record.releaseId);
      setState(record.instanceId, {
        status: 'ready',
        suggestion,
        genres: suggestion.genres,
        year: suggestion.originalYear || '',
      });
    } catch (err) {
      setState(record.instanceId, { status: 'error', message: errorMessage(err) });
    }
  };

  // At most two genre tags, as on the rest of the collection
  const toggleGenre = (instanceId, genre) => {
    const state = stateFor(instanceId);
    let genres = state.genres.filter(g => g !== genre);
    if (genres.length === state.genres.length) genres = [...state.genres, genre].slice(-2);
    setState(instanceId, { ...state, genres });
  };

  const setYear = (instanceId, year) => setState(instanceId, { ...stateFor(instanceId), year });

  // Only fills what is missing; never overwrites tags the record already has
  const apply = async record => {
    const state = stateFor(record.instanceId);
    const { genres: needGenres, year: needYear } = missingFor(record);
    const year = Number(state.year);
    const payload = {
      instanceId: record.instanceId,
      releaseId: record.releaseId,
      folderId: record.folderId,
      ...(needGenres && { genres: state.genres }),
      ...(needYear && { originalYear: year }),
    };

    setState(record.instanceId, { ...state, status: 'saving' });
    try {
      const { folderId } = await applyTags(payload);
      updateRelease(record.instanceId, {
        folderId,
        ...(needGenres && { genres: payload.genres }),
        ...(needYear && {
          originalYear: payload.originalYear,
          hasOriginalYear: true,
          decade: Math.floor(payload.originalYear / 10) * 10,
        }),
      });
      setState(record.instanceId, { status: 'done' });
    } catch (err) {
      setState(record.instanceId, { ...state, status: 'error', message: errorMessage(err) });
    }
  };

  return {
    releases, flagged, isLoading, error, load: loadCollection, stateFor, suggest, toggleGenre, setYear, apply,
  };
}
